import _ from 'underscore';
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Button, mergeClasses, Text } from '@fluentui/react-components';

import { I18nContext } from '@bingads-webui-react/i18n-context';
import { FormCard, FormCardBody } from '@bingads-webui-fluent-component/form-cards';
import {
  AdPreviewCarouselLayoutEx,
  useImageExperienceAdPreviewModel,
} from '@bingads-webui-campaign-react/unified-ad-preview-library-cmui';

import DefaultImage from '../media/default-image.png';
import { useAdPreviwerStyles } from '../styles/ad-previewer-styles';
import { AdPreviewConfig, AdPreviewExtraPlacementInfo, LinkedInImageType, PREVIEW_IMAGE_INDEX } from '../consts';

const isPlaceholderPlacement = placement =>
  _.get(placement, ['imageMeta', 'url']) === DefaultImage;

const getPlacementList = (placementSets, imageType = LinkedInImageType.Landscape) =>
  _.chain(placementSets).findWhere({ placementSetName: imageType }).get('placementList').value();

export const AdPreviewer = ({
  adData,
  adPreviewData,
  adPreviewState: { openEditor },
  isAdEditor,
}) => {
  const i18n = useContext(I18nContext);
  const classes = useAdPreviwerStyles();

  const imageList = useMemo(() => _.get(adData, ['Images', 'ImageList']), [adData]);
  const selectedImageType = useMemo(() => _.get(adData, 'SelectedImageType', LinkedInImageType.Landscape), [adData]);

  const {
    onUpdateImageExperienceImageList,
    imageExperiencePlacementSets,
  } = useImageExperienceAdPreviewModel(AdPreviewConfig, DefaultImage);

  const placementList = useMemo(() =>
    getPlacementList(imageExperiencePlacementSets, selectedImageType), [imageExperiencePlacementSets, selectedImageType]);

  useEffect(() => {
    onUpdateImageExperienceImageList(imageList, PREVIEW_IMAGE_INDEX);
  }, [imageList, onUpdateImageExperienceImageList]);

  const onRenderHeader = useCallback((placement) => {
    const { placementMeta } = placement || {};
    const { placementType } = placementMeta || {};
    const { previewTitle } = AdPreviewExtraPlacementInfo[placementType] || {};

    const showEditButton = !isPlaceholderPlacement(placement) && !isAdEditor;

    return (
      <div className={classes.header}>
        <Text>{i18n.getString(previewTitle)}</Text>
        {showEditButton && (
          <Button onClick={openEditor}>
            {i18n.getString(_TL_('Edit aspect ratio'))}
          </Button>
        )}
      </div>
    );
  }, [classes.header, i18n, isAdEditor, openEditor]);

  const onRenderPlacement = useCallback((placement, AdPlacementComponent) => (
    <div className={classes.placementAndCaptionContainer}>
      <div className={classes.placementContainer}>
        <AdPlacementComponent
          i18n={i18n}
          isEnabled={!isPlaceholderPlacement(placement)}
          placementData={placement}
          {...adPreviewData}
        />
      </div>
      <Text className={classes.caption}>
        {i18n.getString(_TL_('These are examples of how your ads could appear. Depending on placement, the actual ad shown may appear differently.'))}
      </Text>
    </div>
  ), [i18n, adPreviewData, classes.placementAndCaptionContainer, classes.placementContainer, classes.caption]);

  return (
    <FormCard className={mergeClasses('ad-preview-card', classes.root)}>
      <FormCardBody>
        <AdPreviewCarouselLayoutEx
          key={selectedImageType}
          i18n={i18n}
          placementList={placementList}
          useCustomCarousel
          enableAutoPlay={false}
          customCarouselWrapperClassName={classes.carouselWrapper}
          onRenderHeader={onRenderHeader}
          onRenderPlacement={onRenderPlacement}
        />
      </FormCardBody>
    </FormCard>
  );
};

AdPreviewer.propTypes = {
  adData: PropTypes.shape({}).isRequired,
  adPreviewData: PropTypes.shape({}).isRequired,
  adPreviewState: PropTypes.shape({
    openEditor: PropTypes.func,
  }).isRequired,
  isAdEditor: PropTypes.bool,
};

AdPreviewer.defaultProps = {
  isAdEditor: false,
};
